/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The strongest claim that holds for EVERY active device in this response — not merely the org's matching mode. "device": every active device was matched on its own hardware serial, so each one ran the agent. "user": at least one active device was matched only on its assigned-user email, so the set as a whole supports only the weaker claim that assigned users ran the agent somewhere.
 */
export const FleetCoverageAttestation = {
  Device: "device",
  User: "user",
} as const;
/**
 * The strongest claim that holds for EVERY active device in this response — not merely the org's matching mode. "device": every active device was matched on its own hardware serial, so each one ran the agent. "user": at least one active device was matched only on its assigned-user email, so the set as a whole supports only the weaker claim that assigned users ran the agent somewhere.
 */
export type FleetCoverageAttestation = OpenEnum<
  typeof FleetCoverageAttestation
>;

/**
 * Agent coverage and policy enforcement across every device the organization can see: its MDM inventories plus the agent machines no inventory holds. Works with no MDM connected, when every device is an agent-only machine.
 */
export type FleetCoverage = {
  /**
   * Freshness window: an agent counts as active when its heartbeat or state report is within this many minutes.
   */
  activeWindowMinutes: number;
  /**
   * Devices with an agent heartbeat or state report within the window, agent-only machines included.
   */
  agentActive: number;
  /**
   * How many of agent_active are backed by that machine's own heartbeat or state report.
   */
  agentActiveDeviceAttested: number;
  /**
   * Machines running the agent that no connected MDM inventory holds. Always 0 when provider is set.
   */
  agentOnlyDevices: number;
  /**
   * MDM devices whose assigned user runs the agent, but not on this machine. Device-level matching only.
   */
  agentOtherDevice: number;
  /**
   * Devices with a known agent that went quiet.
   */
  agentStale: number;
  /**
   * The strongest claim that holds for EVERY active device in this response — not merely the org's matching mode. "device": every active device was matched on its own hardware serial, so each one ran the agent. "user": at least one active device was matched only on its assigned-user email, so the set as a whole supports only the weaker claim that assigned users ran the agent somewhere.
   */
  attestation: FleetCoverageAttestation;
  /**
   * agent_active devices whose latest state report says the kill switch halted enforcement.
   */
  enforcementHalted: number;
  /**
   * True when the organization has a connected MDM integration (the one named by provider, when set). Without one, the gap buckets are always 0 because nothing lists the devices that lack the agent.
   */
  mdmConnected: boolean;
  /**
   * MDM devices absent from the latest completed inventory snapshot.
   */
  missing: number;
  /**
   * MDM devices whose assigned email resolves to an org member with no agent at all.
   */
  noAgent: number;
  /**
   * MDM devices with no assigned-user email.
   */
  noEmail: number;
  /**
   * Organization the coverage describes.
   */
  organizationId: string;
  /**
   * agent_active devices whose latest report applies the policy last served to the reporting user.
   */
  policyAttested: number;
  /**
   * agent_active devices that report a different policy, or none.
   */
  policyOutdated: number;
  /**
   * agent_active devices with no state report within the window.
   */
  policyUnreported: number;
  /**
   * Every device counted here, missing and agent-only ones included.
   */
  totalDevices: number;
  /**
   * MDM devices whose assigned email matches neither an agent user nor an org member.
   */
  unresolvedEmail: number;
};

/** @internal */
export const FleetCoverageAttestation$inboundSchema: z.ZodMiniType<
  FleetCoverageAttestation,
  unknown
> = openEnums.inboundSchema(FleetCoverageAttestation);

/** @internal */
export const FleetCoverage$inboundSchema: z.ZodMiniType<
  FleetCoverage,
  unknown
> = z.pipe(
  z.object({
    active_window_minutes: z.int(),
    agent_active: z.int(),
    agent_active_device_attested: z.int(),
    agent_only_devices: z.int(),
    agent_other_device: z.int(),
    agent_stale: z.int(),
    attestation: FleetCoverageAttestation$inboundSchema,
    enforcement_halted: z.int(),
    mdm_connected: z.boolean(),
    missing: z.int(),
    no_agent: z.int(),
    no_email: z.int(),
    organization_id: z.string(),
    policy_attested: z.int(),
    policy_outdated: z.int(),
    policy_unreported: z.int(),
    total_devices: z.int(),
    unresolved_email: z.int(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "active_window_minutes": "activeWindowMinutes",
      "agent_active": "agentActive",
      "agent_active_device_attested": "agentActiveDeviceAttested",
      "agent_only_devices": "agentOnlyDevices",
      "agent_other_device": "agentOtherDevice",
      "agent_stale": "agentStale",
      "enforcement_halted": "enforcementHalted",
      "mdm_connected": "mdmConnected",
      "no_agent": "noAgent",
      "no_email": "noEmail",
      "organization_id": "organizationId",
      "policy_attested": "policyAttested",
      "policy_outdated": "policyOutdated",
      "policy_unreported": "policyUnreported",
      "total_devices": "totalDevices",
      "unresolved_email": "unresolvedEmail",
    });
  }),
);

export function fleetCoverageFromJSON(
  jsonString: string,
): SafeParseResult<FleetCoverage, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => FleetCoverage$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'FleetCoverage' from JSON`,
  );
}
